import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authService } from '@/services'
import type { User, LoginRequest, RegisterRequest } from '@/types'

const TOKEN_KEY = 'auth_token'
const USER_KEY = 'auth_user'

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(null)
  const user = ref<User | null>(null)
  const initialized = ref(false)
  const loading = ref(false)

  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const isAdmin = computed(() => user.value?.role === 'admin')
  const isDeveloper = computed(() =>
    user.value?.role === 'developer' || user.value?.role === 'admin'
  )
  const isEmailVerified = computed(() => user.value?.email_verified ?? false)

  async function initialize() {
    if (initialized.value) return

    const storedToken = localStorage.getItem(TOKEN_KEY)
    const storedUser = localStorage.getItem(USER_KEY)

    if (storedToken && storedUser) {
      token.value = storedToken
      try {
        user.value = JSON.parse(storedUser)
        const freshUser = await authService.getMe()
        user.value = freshUser
        localStorage.setItem(USER_KEY, JSON.stringify(freshUser))
      } catch {
        logout()
      }
    }

    initialized.value = true
  }

  async function login(credentials: LoginRequest) {
    loading.value = true
    try {
      const response = await authService.login(credentials)
      token.value = response.token
      user.value = response.user
      localStorage.setItem(TOKEN_KEY, response.token)
      localStorage.setItem(USER_KEY, JSON.stringify(response.user))
      return response
    } finally {
      loading.value = false
    }
  }

  async function register(data: RegisterRequest) {
    loading.value = true
    try {
      return await authService.register(data)
    } finally {
      loading.value = false
    }
  }

  async function verifyEmail(verifyToken: string) {
    loading.value = true
    try {
      const response = await authService.verifyEmail({ token: verifyToken })
      token.value = response.token
      user.value = response.user
      localStorage.setItem(TOKEN_KEY, response.token)
      localStorage.setItem(USER_KEY, JSON.stringify(response.user))
      return response
    } finally {
      loading.value = false
    }
  }

  function logout() {
    token.value = null
    user.value = null
    localStorage.removeItem(TOKEN_KEY)
    localStorage.removeItem(USER_KEY)
  }

  async function refreshUser() {
    if (!token.value) return
    try {
      const freshUser = await authService.getMe()
      user.value = freshUser
      localStorage.setItem(USER_KEY, JSON.stringify(freshUser))
    } catch {
      logout()
    }
  }

  return {
    token,
    user,
    initialized,
    loading,
    isAuthenticated,
    isAdmin,
    isDeveloper,
    isEmailVerified,
    initialize,
    login,
    register,
    verifyEmail,
    logout,
    refreshUser,
  }
})
